import apiClient from "@/api/apiClient";
import { type ArgsGetChecklistTemplate, type ChecklistTemplate } from "@/supabase.types";
import {
  createPaginatedRpcSupabaseQuery,
  createRpcQuery,
  createSearchRpcSupabaseQuery,
  spawnCreateApiFunction,
  spawnUpdateApiFunction,
  type ApiResourceConfig,
} from "@/utils/api";

import type {
  GetChecklistTemplatesBySearchProps,
  GetPaginatedChecklistTemplatesProps,
  PaginatedChecklistTemplate,
} from "@/api/resources/checklistTemplates/types";

const table = "checklist_templates";
const query = `*`;
const querySingle = `*, shared_to:workspaces!checklist_templates_to_workspaces(id)`;

export const apiConfigChecklistTemplates: ApiResourceConfig<typeof table, typeof query> = {
  table,
  query,
  searchables: ["title", "description"],
  sortables: ["created_at", "updated_at", "title", "is_public"],
};

export const createChecklistTemplate = spawnCreateApiFunction({ table, query });
export const updateChecklistTemplate = spawnUpdateApiFunction({ table, query });

export async function getChecklistTemplate(match: Partial<ChecklistTemplate>) {
  const { data, error } = await apiClient
    .from(table)
    .select(querySingle)
    .match(match)
    .eq("is_deleted", false)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export const deleteChecklistTemplate = async (match: Partial<ChecklistTemplate>) => {
  const { error, ...rest } = await apiClient.from(table).update({ is_deleted: true }).match(match);
  if (error) throw error;

  return { error, ...rest };
};

export const getChecklistTemplates = async (args: ArgsGetChecklistTemplate) => {
  const { data, error } = await apiClient.rpc("get_checklist_templates", args, { count: "estimated" });

  if (error) throw error;
  return data;
};

export const getAvailableChecklistTemplates = (workspace_id: string) =>
  getChecklistTemplates({ _workspace_id: workspace_id, _with_local: true, _with_shared: true, _with_public: true });

export const getLocalChecklistTemplates = (workspace_id: string) =>
  getChecklistTemplates({ _workspace_id: workspace_id, _with_local: true });

export const getSharedChecklistTemplates = (workspace_id: string) =>
  getChecklistTemplates({ _workspace_id: workspace_id, _with_local: false, _with_shared: true });

export const getPublicChecklistTemplates = (workspace_id: string) =>
  getChecklistTemplates({ _workspace_id: workspace_id, _with_local: false, _with_public: true });

export const getPaginatedChecklistTemplates = async ({
  dataTableOptions,
  customFilters,
  customOrFilters,
  ...args
}: GetPaginatedChecklistTemplatesProps) => {
  const { data, count, error } = await createPaginatedRpcSupabaseQuery<"get_checklist_templates">({
    baseOptions: {
      rpcQuery: createRpcQuery("get_checklist_templates", args, { count: "estimated" }),
      searchables: apiConfigChecklistTemplates.searchables,
      sortables: apiConfigChecklistTemplates.sortables,
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });
  if (error) throw error;
  const _data = data as PaginatedChecklistTemplate[] | null;
  return { data: _data, count };
};

export async function getChecklistTemplatesBySearch({
  searchOptions,
  customFilters,
  customOrFilters,
  globalFilters,
  limit,
  ...args
}: GetChecklistTemplatesBySearchProps) {
  const { data, count, error } = await createSearchRpcSupabaseQuery({
    baseOptions: {
      rpcQuery: createRpcQuery("get_checklist_templates", args, { count: "estimated" }),
      searchables: apiConfigChecklistTemplates.searchables,
      sortables: apiConfigChecklistTemplates.sortables,
    },
    searchOptions,
    customFilters,
    customOrFilters,
    limit,
  });
  if (error) throw error;
  const _data = data as PaginatedChecklistTemplate[] | null;
  return { data: _data, count };
}
